import { ShieldCheck, FlaskConical, MapPin, Sparkles } from "lucide-react";

/**
 * Curriculum-standards trust badges. Rendered on the marketing pages and
 * signed-out flows so educators immediately see the alignment guarantees.
 *
 * variant = "light" → pill chips on any background (default)
 * variant = "onDark" → glass chips over the dark auth hero
 * variant = "compact" → smaller horizontal strip for tight spaces
 */
const STANDARDS = [
  {
    id: "common-core",
    label: "Common Core",
    sub: "ELA · Math",
    icon: ShieldCheck,
    title: "Aligned with the Common Core State Standards (ELA/Literacy & Math). Every quiz question is auto-tagged to a real CCSS code.",
  },
  {
    id: "ngss",
    label: "NGSS",
    sub: "Science",
    icon: FlaskConical,
    title: "Aligned with the Next Generation Science Standards (K-12). Life, Physical, Earth & Engineering practices covered.",
  },
  {
    id: "teks",
    label: "TEKS",
    sub: "Texas",
    icon: MapPin,
    title: "Aligned with the Texas Essential Knowledge and Skills. Districts in TX can trust Lectura's coverage out of the box.",
  },
];

export const StandardsBadges = ({ variant = "light", showHeader = true }) => {
  if (variant === "onDark") {
    return (
      <div className="flex flex-wrap gap-2" data-testid="standards-badges-dark">
        {STANDARDS.map((s) => {
          const Icon = s.icon;
          return (
            <span
              key={s.id}
              title={s.title}
              data-testid={`standard-badge-${s.id}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-primary-foreground/15 backdrop-blur px-3 py-1 text-[10px] font-bold uppercase tracking-wider"
            >
              <Icon className="h-3 w-3" /> {s.label}
            </span>
          );
        })}
      </div>
    );
  }

  if (variant === "compact") {
    return (
      <div className="flex flex-wrap gap-1.5" data-testid="standards-badges-compact">
        {STANDARDS.map((s) => {
          const Icon = s.icon;
          return (
            <span
              key={s.id}
              title={s.title}
              data-testid={`standard-badge-${s.id}`}
              className="inline-flex items-center gap-1 rounded-full bg-accent/10 text-accent border border-accent/30 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
            >
              <Icon className="h-3 w-3" /> {s.label}
            </span>
          );
        })}
      </div>
    );
  }

  return (
    <div data-testid="standards-badges" className="space-y-2">
      {showHeader && (
        <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-accent">
          <Sparkles className="h-3 w-3" /> Standards-Aligned by Gemini 3.1 Pro
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {STANDARDS.map((s) => {
          const Icon = s.icon;
          return (
            <span
              key={s.id}
              title={s.title}
              data-testid={`standard-badge-${s.id}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-accent/40 bg-accent/10 text-accent px-3 py-1 text-[11px] font-bold uppercase tracking-wider hover:bg-accent/20 transition"
            >
              <Icon className="h-3.5 w-3.5" />
              <span>{s.label}</span>
              <span className="text-[9px] font-medium normal-case tracking-normal opacity-70">· {s.sub}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
};
